import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import { AssessmentReportEarlyOptions } from '@utils/Variables'
import { useDropdownMenu } from '@utils/assessmentReportUtils'
import { objToLevSelOpts } from '@utils/enumHelper'
import { appSessionStorage } from '@utils/storageUtils'
import { Flex, Spin } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Database_Procs_TeamBonusGames_Item } from '@suntex.fim/api-client'
import { useFramework } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { ExportMenu, LevelSelect, SearchInput } from '@suntex.fim/ui'

import columns, {
  getEarlyColumnKeys,
  hidableSelectLabel
} from './AssessmentReport/AssessmentReportEarlyTable'
import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

const levelOptions = objToLevSelOpts(AssessmentReportEarlyOptions)
/**
 * AssessmentReportEarly Component
 * @returns JSX for early report
 */
function AssessmentReportEarly() {
  const { api } = useFramework()
  const { earlyLevel } = useAppQueryString()
  const team = useOptionalTeamInfo()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [filters, setFilters] = useState({
    level: Number(earlyLevel) || appSessionStorage.earlyLevel
  })
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  // Reusable function for opening modal and setting send message data
  const sendMessage = useCallback((data: any[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }, [])
  const memoColumns = useMemo<ColumnType<Database_Procs_TeamBonusGames_Item>[]>(
    () => columns({ level: filters.level, graphColor: '#3FB2FF', sendMessage }),
    [filters.level, sendMessage]
  )

  const { data: assessmentReport = [], isLoading } = useQuery({
    queryKey: ['assessment', 'early', team?.id],
    queryFn: async () => {
      const response = await api.get_Assessment_BonusTeam({
        teamId: team!.id
      })
      return response.teamBonusGames || []
    },
    enabled: team?.id != null
  })

  const { table, Table } =
    useAssessmentTable<Database_Procs_TeamBonusGames_Item>({
      data: assessmentReport,
      columns: memoColumns,
      isLoading,
      globalFilter: searchQuery,
      initialSorting: [{ id: 'displayName', desc: false }],
      horizontalScroll: true
    })
  const selectedRow = table.getSelectedRowModel().flatRows

  useEffect(() => {
    appSessionStorage.earlyLevel = filters.level
  }, [filters.level])
  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: Database_Procs_TeamBonusGames_Item }) => ({
      label: original.displayName || original.userId,
      value: original.playerId
    })
  )
  const columnKeys = getEarlyColumnKeys(filters.level)
  const linkOptions = columnKeys.map((label) => ({
    key: `word-problems/${filters.level}/${label}`,
    label: hidableSelectLabel[label]
  }))
  const items = useDropdownMenu({
    table,
    hidableSelectLabel,
    fileName: `${team?.name}_Early`,
    setIsLoading: setIsExporting
  })
  return (
    <Flex vertical gap="20px" style={{ position: 'relative' }}>
      {/* Loader overlay when loading and popup not open */}
      {(isLoading || isExporting) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Spin size="large" tip="Loading..." />
        </div>
      )}
      <AssessmentReportNavigation
        exportButton={
          <ExportMenu
            ariaLabel="Export report options"
            items={items}
            trigger={['click']}
          />
        }
        filters={
          <Flex gap={14}>
            <Flex gap={6} flex="1 1 auto" vertical>
              <p className="dd-label fw-bold text-sm">Level</p>
              <LevelSelect
                value={filters.level}
                onChange={(level) => {
                  setFilters((prev) => ({ ...prev, level }))
                  setSearchQuery('')
                }}
                options={levelOptions}
              />
            </Flex>
          </Flex>
        }
      />
      <Flex className="tiles-container" wrap="wrap">
        <Flex className="left" vertical gap="20px" flex="1 1 560px">
          <AssessmentReportSummaryDashboard />
        </Flex>
      </Flex>
      <div className="main-result">
        <Flex
          justify="space-between"
          align="center"
          className="main-result__toolbar"
          wrap
        >
          <h4 className="title">Team Players</h4>
          <Flex
            gap="10px"
            justify="space-between"
            wrap
            className="export-btn print-hidden"
          >
            <div className="report-search">
              <SearchInput value={searchQuery} onChange={setSearchQuery} />
            </div>
            <Button
              intent="tertiary"
              size="small"
              disabled={isDisabled}
              onClick={() => setIsModalOpen(true)}
            >
              SEND MESSAGE
            </Button>
          </Flex>
        </Flex>
        <Table />
      </div>
      {!isModalOpen && (
        <SelectedRowPopUp
          selectedRow={selectedRow}
          resetRowSelection={table.resetRowSelection}
          handleSendMessage={() => setIsModalOpen(true)}
        />
      )}
      <SendMessage
        level={levelOptions[filters.level]?.label}
        linkOptions={linkOptions}
        isModalOpen={isModalOpen}
        closeModal={(isSuccess) => {
          setIsModalOpen(false)
          setSendMessageData([])
          if (isSuccess) {
            table.resetRowSelection()
          }
        }}
        selectedUserOptions={selectedUserOptions}
        recipientEntityId={team?.id}
        reportType="Early"
      />
    </Flex>
  )
}

export default AssessmentReportEarly
